import { type SetRule, SET_RULE_PRESETS, setState, setsWinner, teamTieWinner } from "../competition/ranking";
import {
	RESULT_TEAM_STAT_MAX,
	SETS_BEST_OF_MAX,
	type SaveResultsBody,
	type SetsMeetConfig,
	type SetsResultDetail,
	TEAM_STAT_CODES_BY_SPORT,
} from "../competition/results";
import type { MeetOutcome } from "../competition/vocab";
import type { TsrCompletedSet, TsrLiveMatchState, TsrMatchStats, TsrStatsEvent, TsrTeam } from "./socket";

/**
 * Save Meet for a TSR set / rally match (sepak takraw, badminton, table
 * tennis, tennis, squash, volleyball, lawn bowls): the controller's live
 * state → the SETS family body of the EXISTING `PATCH /api/meets/:id/results`,
 * for both slots. Pure: the TSR client calls it and sends the body itself, as
 * the signed-in operator, so the route's group / sport / permission check and
 * the slot versions apply exactly as for an OVR save. Nothing here talks to a
 * server, and the API has no other path that persists a live score.
 *
 * Each slot gets what OVR's SETS board would derive from the same scores
 * (apps/ovr/src/families/sets/logic.ts `deriveMatch`), so a TSR save and an
 * OVR save of the same match are byte-identical:
 *
 *   detail.periods   that side's score per set, `{ seq, score }` — the banked
 *                    sets, then the set in progress when it has a score. A
 *                    tennis tie-break set also carries `tiebreak` (both sides'
 *                    tie-break points: the loser's as TSR kept them, the
 *                    winner's the least that wins — max(7, loser + 2))
 *   detail.serving   whether that side serves now — only while the match is
 *                    live: a finished match has no server, and OVR (which
 *                    never sets it) stores none
 *   detail.team_stats  STK codes from the Stats Controller log, when given:
 *                    the match, then each set (`period`)
 *   result_value     sets won (COMPLETE sets only, under the sport's rule);
 *                    in ENDS mode (lawn bowls) the total of the periods
 *   result_display   own score first, en dash, one set per word: "15–6 15–8",
 *                    a tie-break "7–6(7–4)"; ENDS mode the totals "18–12"
 *   outcome          WIN / LOSS from `setsWinner` (majority of `best_of`); null while undecided
 *   rank             1 for the winner, 2 for the loser; null while undecided
 *   points           only with a points table (a POINTS_SUM group meet): table[outcome]
 *
 * `detail` is a whole-value replace on the server, so the slot's CURRENT
 * detail (as loaded) is the base: keys this mapper does not own (`extra`,
 * `shootout`, typed `team_stats` when no stats log is given) are kept;
 * `periods` is always replaced whole — an undone set is gone, not merged.
 *
 * A TEAM TIE (badminton / table tennis / tennis / squash team events: a
 * parent meet whose rubbers are child meets) is `tsrTeamTieSaveBodies`: one
 * SETS body per rubber played, from the controller's games, and the tie's
 * own headline (rubbers won, `teamTieWinner`) — each sent to that meet's own
 * `PATCH /meets/:id/results`.
 */

export const TSR_SEPAK_TAKRAW_RULE: SetRule = SET_RULE_PRESETS.SEPAK_TAKRAW;

/**
 * Set rules for the TSR sports `SET_RULE_PRESETS` does not carry (that list
 * is OVR's rule picker, labelled per key; these stay TSR's until OVR has a
 * profile for the sport):
 *
 *   SQUASH       11, by 2 (point-a-rally)        — WSF Rules of Singles Squash, rule 2
 *   TENNIS       a set in GAMES: 6, by 2, at 6–6 a tie-break decides it 7–6
 *                (`cap` 7: 7–5 and 7–6 complete, 7–4 impossible) — ITF Rules of
 *                Tennis 5 (b) "tie-break set"; the tie-break's own points are the
 *                period's `tiebreak`
 *   TENNIS_MATCH_TIEBREAK  the same sets, the deciding one replaced by a match
 *                tie-break to 10, by 2 (its POINTS are the period's score) — ITF
 *                Rules of Tennis, Appendix V (alternative procedures), doubles
 */
export const TSR_SET_RULES = {
	SQUASH: { points: 11, winBy: 2 },
	TENNIS: { points: 6, winBy: 2, cap: 7 },
	TENNIS_MATCH_TIEBREAK: { points: 6, winBy: 2, cap: 7, decider: { points: 10, winBy: 2 } },
} as const satisfies Record<string, SetRule>;

export type TsrPointsTable = Readonly<Record<"WIN" | "DRAW" | "LOSS", number>>;

// ─── Per-sport rules ─────────────────────────────────────────────────────────

/**
 * How a sport's set / rally match is saved:
 *
 *   rule           the set rule (`SET_RULE_PRESETS`); absent = any lead wins a
 *                  banked set (no rule we are sure of)
 *   mode           best_of — sets won decide (every racket and net sport);
 *                  ends — the periods are ENDS and their total decides (lawn
 *                  bowls: shots per end). A meet whose `config.best_of` is set
 *                  is always best-of (lawn bowls sets play).
 *   defaultBestOf  when neither the meet nor the controller says (OVR's `suggest`)
 *   points         group-table match points (POINTS_SUM phase, top-level meet only)
 *   ties           its team events are ties of rubbers (child meets)
 */
export interface TsrSetsSportProfile {
	rule?: SetRule;
	mode: "best_of" | "ends";
	defaultBestOf: number;
	points?: TsrPointsTable;
	ties: boolean;
}

/**
 * Keyed by the SEA Games `sports.code` (the API's game-settings seed). The
 * rules and points are OVR's `SPORT_PROFILES` (apps/ovr/src/families/sets/
 * logic.ts) where OVR has the sport, so the two never disagree:
 *
 *   STK sepak takraw  15 by 2 cap 17, best of 3; group 2 points a win (OVR)
 *   BD  badminton     21 by 2 cap 30, best of 3; group ties 1 point a win (OVR); ties of 5 rubbers
 *   TTE table tennis  11 by 2, best of 5 (7 for an individual semi / final — the meet's config); ties
 *   TEN tennis        games: 6 by 2, 7–6 by tie-break; best of 3; doubles' deciding set a
 *                     match tie-break to 10 (`tsrSetRuleFor`); ties (single / doubles / single)
 *   SQU squash        11 by 2, best of 5; team event of three singles rubbers
 *   VBL volleyball    25 by 2, deciding set 15 by 2; indoor best of 5, beach best of 3
 *                     (the meet's config, else the controller's maxSets)
 *   LBW lawn bowls    ENDS: the shots of every end summed, the higher total wins
 *                     (World Bowls Laws of the Sport: a game is a number of ends);
 *                     sets play only when the meet's config has best_of
 *
 * No points table outside BD / STK: OVR writes none for those sports either.
 */
export const TSR_SETS_SPORTS: Readonly<Record<string, TsrSetsSportProfile>> = {
	STK: { rule: SET_RULE_PRESETS.SEPAK_TAKRAW, mode: "best_of", defaultBestOf: 3, points: { WIN: 2, DRAW: 0, LOSS: 0 }, ties: false },
	BD: { rule: SET_RULE_PRESETS.BADMINTON, mode: "best_of", defaultBestOf: 3, points: { WIN: 1, DRAW: 0, LOSS: 0 }, ties: true },
	TTE: { rule: SET_RULE_PRESETS.TABLE_TENNIS, mode: "best_of", defaultBestOf: 5, ties: true },
	TEN: { rule: TSR_SET_RULES.TENNIS, mode: "best_of", defaultBestOf: 3, ties: true },
	SQU: { rule: TSR_SET_RULES.SQUASH, mode: "best_of", defaultBestOf: 5, ties: true },
	VBL: { rule: SET_RULE_PRESETS.VOLLEYBALL, mode: "best_of", defaultBestOf: 5, ties: false },
	LBW: { mode: "ends", defaultBestOf: 1, ties: false },
};

/** The sport's profile, or null for a sport this mapper does not know. */
export function tsrSetsProfile(sportCode: string | null | undefined): TsrSetsSportProfile | null {
	return (sportCode && TSR_SETS_SPORTS[sportCode.toUpperCase()]) || null;
}

/**
 * The set rule for this match: the sport's, except a tennis match the
 * controller plays with a deciding MATCH tie-break (doubles), whose last set
 * is that tie-break's points (10–7), not games.
 */
export function tsrSetRuleFor(sportCode: string | null | undefined, state?: Record<string, unknown> | null): SetRule | undefined {
	const code = sportCode?.toUpperCase();
	if (code === "TEN" && state?.superTiebreakDecider === true) return TSR_SET_RULES.TENNIS_MATCH_TIEBREAK;
	return tsrSetsProfile(code)?.rule;
}

const isInt = (v: unknown): v is number => typeof v === "number" && Number.isInteger(v) && v >= 0;

/**
 * best-of for the match: the meet's config, else the controller's. A tennis
 * doubles match the controller plays as "best of two, a match tie-break at
 * one set all" is a best of THREE on the result sheet (the tie-break is the
 * third set).
 */
function bestOfFor(state: TsrLiveMatchState, config: SetsMeetConfig | null | undefined, fallback: number): number {
	if (config?.best_of !== undefined) return config.best_of;
	const max = isInt(state.maxSets) && state.maxSets > 0 ? state.maxSets : fallback;
	if (state.superTiebreakDecider === true && max % 2 === 0) return max + 1;
	return max;
}

// ─── Inputs / outputs ────────────────────────────────────────────────────────

/** One slot of the meet as loaded (`GET /meets/:id`): its id, its version, its current detail. */
export interface TsrSaveSlot {
	meet_competitor_id: string;
	version: number;
	detail?: SetsResultDetail | Record<string, unknown> | null;
}

export interface TsrSetsSaveInput {
	/** The controller's state at Save Meet. */
	state: TsrLiveMatchState;
	/** `[side A, side B]` — A is the first slot by start_order (`tsrOrderSlots`). */
	slots: readonly [TsrSaveSlot, TsrSaveSlot];
	/** `meets.config` — `best_of` decides the match. Default `{ best_of: state.maxSets }`. */
	config?: SetsMeetConfig | null;
	/** The Stats Controller log (`get_match_stats`), for `team_stats`. Omit to leave them alone. */
	stats?: TsrMatchStats | null;
	/** Match points per outcome, only for a POINTS_SUM group meet (OVR's STK profile: WIN 2, LOSS 0). Omit: `points` is not sent. */
	points?: TsrPointsTable | null;
	/**
	 * The meet's `sports.code` (STK, BD, TTE, TEN, SQU, VBL, LBW): picks the rule
	 * and the mode from `TSR_SETS_SPORTS`. A sport not listed is scored
	 * without a rule. Without `sport` (and without `rule`): sepak takraw's.
	 */
	sport?: string | null;
	/** Overrides the sport's rule. Default: `tsrSetRuleFor(sport)`, else `SET_RULE_PRESETS.SEPAK_TAKRAW`. */
	rule?: SetRule;
}

export type TsrSetsWarning =
	/** A set score the rule says is impossible (16–10, 18–16): saved as typed, the board warns. */
	| { code: "INVALID_SET"; seq: number }
	/** The set in progress was saved as a period (it is not COMPLETE, so it counts for nobody). */
	| { code: "LIVE_SET_INCLUDED"; seq: number }
	/** A set played after the match was already decided. */
	| { code: "SET_AFTER_DECIDED"; seq: number }
	/** Nobody has the majority yet: outcome / rank / points are sent as null. */
	| { code: "UNDECIDED" }
	/** The controller says finished, the scores do not decide it (or the other way round). */
	| { code: "FINISHED_MISMATCH" };

export interface TsrSetsSaveResult {
	body: SaveResultsBody;
	/** Sets won by [A, B] (ENDS mode: the totals) and their outcomes (null while undecided). */
	verdict: { sets: [number, number]; outcomes: [MeetOutcome, MeetOutcome] | null };
	warnings: TsrSetsWarning[];
}

export class TsrMappingError extends Error {
	constructor(
		readonly code: "MULTI_GAME_UNSUPPORTED" | "SLOTS_REQUIRED" | "NOT_A_TIE" | "RUBBERS_MISSING" | "RUBBER_SLOTS",
		message: string,
	) {
		super(message);
		this.name = "TsrMappingError";
	}
}

const DASH = "–"; // en dash, as OVR and the stored results print it

// ─── A / B ───────────────────────────────────────────────────────────────────

/** A slot as the API's resolver reads it (`competitor_id`) or as `GET /meets/:id` returns it (`competitor`). */
type SlotOrderRow = {
	meet_competitor_id: string;
	competitor_id?: string | null;
	competitor?: { competitor_id: string } | null;
	start_order?: number | null;
	side?: string | null;
};

const SIDE_RANK: Readonly<Record<string, number>> = { HOME: 0, AWAY: 1 };

const competitorOf = (s: SlotOrderRow): string | null => s.competitor_id ?? s.competitor?.competitor_id ?? null;

/**
 * The board order of a meet's slots — the ONE rule, shared with the API's
 * resolver (apps/api `services/tsr/teams.service.ts`) and the app's
 * `orderedSlots`: `start_order` (nulls last), then HOME before AWAY, then id.
 * A comparator, so a caller ordering every slot (filled or not) and one
 * picking the two sides can never disagree on which is A.
 */
export function tsrCompareSlots(x: SlotOrderRow, y: SlotOrderRow): number {
	const key = (s: SlotOrderRow) => [s.start_order ?? Number.MAX_SAFE_INTEGER, s.side != null ? (SIDE_RANK[s.side.toUpperCase()] ?? 2) : 3] as const;
	const [xo, xs] = key(x);
	const [yo, ys] = key(y);
	return xo - yo || xs - ys || (x.meet_competitor_id < y.meet_competitor_id ? -1 : x.meet_competitor_id > y.meet_competitor_id ? 1 : 0);
}

/** Whether a slot names a competitor — a slot still waiting for its source is not a side. */
export const tsrSlotFilled = (s: SlotOrderRow): boolean => competitorOf(s) !== null;

/**
 * Which slot is side A and which is B (`tsrCompareSlots`). Exactly two filled
 * slots or null: scoring the wrong side is worse than scoring none.
 */
export function tsrOrderSlots<T extends SlotOrderRow>(slots: readonly T[]): [T, T] | null {
	const filled = slots.filter(tsrSlotFilled);
	if (filled.length !== 2) return null;
	const sorted = [...filled].sort(tsrCompareSlots);
	return [sorted[0]!, sorted[1]!];
}

// ─── The sets a game holds ───────────────────────────────────────────────────

/** One set as the mapper reads it: [A, B] score, and a tennis tie-break's [A, B] points. */
interface MappedSet {
	score: [number, number];
	tiebreak: [number, number] | null;
}

/**
 * A tennis tie-break set (TSR keeps the LOSER's tie-break points only, as
 * the "7–6 (5)" on the board does): both sides' points, the winner's being
 * the least that wins a 7-point tie-break — max(7, loser + 2). ITF Rules 5 (b).
 */
function tiebreakOf(s: TsrCompletedSet): [number, number] | null {
	const lost = s.tbLoserPoints;
	if (!isInt(lost) || s.a === s.b) return null;
	const won = Math.max(7, lost + 2);
	return s.a > s.b ? [won, lost] : [lost, won];
}

function bankedSets(list: readonly TsrCompletedSet[] | null | undefined): MappedSet[] {
	const out: MappedSet[] = [];
	for (const s of list ?? []) {
		if (!isInt(s?.a) || !isInt(s?.b)) continue;
		out.push({ score: [s.a, s.b], tiebreak: tiebreakOf(s) });
	}
	return out;
}

/** The [A, B] set scores the state's CURRENT game holds, banked sets first, then the live set when it has a score. */
function currentGameSets(state: TsrLiveMatchState): { sets: MappedSet[]; liveSeq: number | null } {
	const sets = bankedSets(state.completedSets);
	const a = isInt(state.scoreA) ? state.scoreA : 0;
	const b = isInt(state.scoreB) ? state.scoreB : 0;
	if (a === 0 && b === 0) return { sets, liveSeq: null };
	sets.push({ score: [a, b], tiebreak: null });
	return { sets, liveSeq: sets.length };
}

const STK_CODES = TEAM_STAT_CODES_BY_SPORT.STK;

/**
 * The STK team statistics one side earned, from the Stats Controller log:
 * the whole match (no `period`), then each set a point was logged in
 * (`period` = the event's `set`; `TEAM_STATS_BY_SET_SPORTS`). Both sides get
 * the same sets, a code 0 where nothing was logged; a point with no set
 * counts for the match only. An event's `team` is the side CREDITED with the
 * point, so a service fault credited to B is A's fault.
 */
export function tsrSepakTakrawTeamStats(stats: TsrMatchStats | null | undefined, side: TsrTeam): NonNullable<SetsResultDetail["team_stats"]> {
	const events: TsrStatsEvent[] = Array.isArray(stats?.events) ? stats.events.filter((e) => e !== null && typeof e === "object") : [];
	const other: TsrTeam = side === "A" ? "B" : "A";
	const setOf = (e: TsrStatsEvent): number | null => (typeof e.set === "number" && Number.isInteger(e.set) && e.set >= 1 && e.set <= SETS_BEST_OF_MAX ? e.set : null);
	const codesOf = (list: readonly TsrStatsEvent[]) => {
		const count = (team: TsrTeam, action: string) => list.filter((e) => e.team === team && e.action === action).length;
		const values: Record<(typeof STK_CODES)[number], number> = {
			service_aces: count(side, "ace"),
			service_faults: count(other, "service-fault"),
			spike_points: count(side, "spike"),
			block_points: count(side, "block"),
		};
		return STK_CODES.map((code) => ({ code, value: Math.min(values[code], RESULT_TEAM_STAT_MAX) }));
	};
	const credited = events.filter((e) => e.team === "A" || e.team === "B");
	const sets = [...new Set(credited.map(setOf).filter((n): n is number => n !== null))].sort((x, y) => x - y);
	return [...codesOf(events), ...sets.flatMap((period) => codesOf(events.filter((e) => setOf(e) === period)).map((s) => ({ ...s, period })))];
}

function rankOf(outcome: MeetOutcome | null): number | null {
	if (outcome === "WIN" || outcome === "BYE" || outcome === "DRAW") return 1;
	if (outcome === "LOSS") return 2;
	return null;
}

const pointsOf = (table: TsrPointsTable, outcome: MeetOutcome | null): number | null =>
	outcome === null ? null : outcome === "BYE" ? table.WIN : table[outcome];

// ─── One two-slot SETS meet from a list of sets ──────────────────────────────

interface MatchSpec {
	sets: MappedSet[];
	liveSeq: number | null;
	slots: readonly [TsrSaveSlot, TsrSaveSlot];
	bestOf: number;
	/** ENDS mode: the periods are summed (no best_of). */
	ends: boolean;
	rule: SetRule | undefined;
	/** The side serving now, or null (a finished game, a past rubber). */
	serving: TsrTeam | null;
	finished: boolean;
	stats?: TsrMatchStats | null | undefined;
	points?: TsrPointsTable | null | undefined;
	config?: SetsMeetConfig | null | undefined;
}

function omitBestOf(config: SetsMeetConfig | null | undefined): SetsMeetConfig {
	const { best_of: _b, ...rest } = config ?? {};
	return rest;
}

function mapMatch(m: MatchSpec): TsrSetsSaveResult {
	const { sets, liveSeq, rule } = m;
	const config: SetsMeetConfig = m.ends ? omitBestOf(m.config) : { ...(m.config ?? {}), best_of: m.bestOf };
	const warnings: TsrSetsWarning[] = [];

	const periodsOf = (i: 0 | 1) =>
		sets.map((s, n) => (s.tiebreak ? { seq: n + 1, score: s.score[i], tiebreak: s.tiebreak[i] } : { seq: n + 1, score: s.score[i] }));
	const details = ([0, 1] as const).map((i) => {
		const current = (m.slots[i].detail ?? {}) as SetsResultDetail;
		const side: TsrTeam = i === 0 ? "A" : "B";
		const next: SetsResultDetail = { ...current, periods: periodsOf(i), serving: m.serving === side };
		if (m.finished || m.serving === null) delete next.serving;
		if (sets.length === 0) delete next.periods;
		if (m.stats) next.team_stats = tsrSepakTakrawTeamStats(m.stats, side);
		return next;
	}) as [SetsResultDetail, SetsResultDetail];

	if (!m.ends && rule) {
		sets.forEach(({ score: [a, b] }, n) => {
			const seq = n + 1;
			if (setState(a, b, rule, seq === m.bestOf) === "INVALID") warnings.push({ code: "INVALID_SET", seq });
		});
	}
	if (liveSeq !== null) warnings.push({ code: "LIVE_SET_INCLUDED", seq: liveSeq });

	const verdict = setsWinner([{ detail: details[0], irm: null }, { detail: details[1], irm: null }], config, m.ends ? undefined : rule);
	// ENDS: the verdict names whoever is ahead after every end; the game is decided only when the controller says it is over.
	let outcomes = verdict.outcomes as [MeetOutcome, MeetOutcome] | null;
	if (m.ends && (!m.finished || sets.length === 0)) outcomes = null;
	if (outcomes === null) warnings.push({ code: "UNDECIDED" });
	if (!m.ends && (outcomes !== null) !== m.finished) warnings.push({ code: "FINISHED_MISMATCH" });

	if (!m.ends) {
		// A set after the deciding one: the match was won before it was played.
		const majority = Math.floor(m.bestOf / 2) + 1;
		const won: [number, number] = [0, 0];
		sets.forEach(({ score: [a, b], tiebreak }, n) => {
			if (won[0] >= majority || won[1] >= majority) warnings.push({ code: "SET_AFTER_DECIDED", seq: n + 1 });
			else if (!rule || setState(a, b, rule, n + 1 === m.bestOf) === "COMPLETE") {
				if (a !== b) won[a > b ? 0 : 1]++;
				else if (tiebreak && tiebreak[0] !== tiebreak[1]) won[tiebreak[0] > tiebreak[1] ? 0 : 1]++;
			}
		});
	}

	const display = (me: 0 | 1): string | null => {
		if (sets.length === 0) return null;
		const them = me === 0 ? 1 : 0;
		if (m.ends) return `${verdict.score[me]}${DASH}${verdict.score[them]}`;
		return sets.map((s) => `${s.score[me]}${DASH}${s.score[them]}${s.tiebreak ? `(${s.tiebreak[me]}${DASH}${s.tiebreak[them]})` : ""}`).join(" ");
	};

	const results = ([0, 1] as const).map((i) => {
		const outcome = outcomes ? outcomes[i] : null;
		const row: SaveResultsBody["results"][number] = {
			meet_competitor_id: m.slots[i].meet_competitor_id,
			version: m.slots[i].version,
			result_value: sets.length > 0 || outcome !== null ? verdict.score[i] : null,
			result_display: display(i),
			outcome,
			rank: rankOf(outcome),
			detail: details[i] as Record<string, unknown>,
		};
		if (m.points) row.points = pointsOf(m.points, outcome);
		return row;
	});

	return { body: { results }, verdict: { sets: [verdict.score[0], verdict.score[1]], outcomes }, warnings };
}

interface Resolved {
	rule: SetRule | undefined;
	ends: boolean;
	bestOf: number;
}

function resolve(state: TsrLiveMatchState, config: SetsMeetConfig | null | undefined, sport: string | null | undefined, rule: SetRule | undefined): Resolved {
	const profile = tsrSetsProfile(sport);
	const named = sport !== undefined && sport !== null;
	return {
		rule: rule ?? (named ? tsrSetRuleFor(sport, state as Record<string, unknown>) : TSR_SEPAK_TAKRAW_RULE),
		ends: profile?.mode === "ends" && config?.best_of === undefined,
		bestOf: bestOfFor(state, config, profile?.defaultBestOf ?? 3),
	};
}

const servingOf = (state: TsrLiveMatchState): TsrTeam | null => (state.servingTeam === "A" || state.servingTeam === "B" ? state.servingTeam : null);

/** One two-slot match (a single game — not a team tie). */
export function tsrSetsSaveBody(input: TsrSetsSaveInput): TsrSetsSaveResult {
	const { state } = input;
	if (state.multiGame) {
		throw new TsrMappingError("MULTI_GAME_UNSUPPORTED", "A multi-game team tie is saved per rubber (`tsrTeamTieSaveBodies`), not by this mapper");
	}
	if (!input.slots?.[0] || !input.slots?.[1]) throw new TsrMappingError("SLOTS_REQUIRED", "Both slots (A, B) are required");

	const r = resolve(state, input.config, input.sport, input.rule);
	const { sets, liveSeq } = currentGameSets(state);
	return mapMatch({
		sets,
		liveSeq,
		slots: input.slots,
		bestOf: r.bestOf,
		ends: r.ends,
		rule: r.rule,
		serving: servingOf(state),
		finished: Boolean(state.matchFinished),
		stats: input.stats,
		points: input.points,
		config: input.config,
	});
}

// ─── Team ties ───────────────────────────────────────────────────────────────

/** A slot of a tie or a rubber as loaded (`GET /meets/:id` → `competitors` / `children[].competitors`). */
export interface TsrTieSlot extends TsrSaveSlot {
	competitor_id: string | null;
	/** The outcome it holds now — a rubber not played in this state keeps it. */
	outcome?: MeetOutcome | null;
}

export interface TsrTieRubber {
	meet_id: string;
	/** `meets.sequence` within the tie: game 1 of the controller is the lowest. */
	sequence: number;
	config?: SetsMeetConfig | Record<string, unknown> | null;
	slots: readonly TsrTieSlot[];
}

export interface TsrTieSaveInput {
	state: TsrLiveMatchState;
	sport: string;
	tie: {
		meet_id: string;
		/** `[side A, side B]` of the TIE — a rubber's sides are matched to these by competitor. */
		slots: readonly [TsrTieSlot, TsrTieSlot];
		/** The tie's config — `best_of` rubbers (best of five). Default: the number of rubbers. */
		config?: SetsMeetConfig | Record<string, unknown> | null;
		/** Tie points (a POINTS_SUM group meet; BD: 1 a tie won). Omit: `points` is not sent. */
		points?: TsrPointsTable | null;
	};
	rubbers: readonly TsrTieRubber[];
}

export interface TsrRubberSave extends TsrSetsSaveResult {
	meet_id: string;
	/** The controller's game number (1-based). */
	game: number;
}

export interface TsrTieSaveResult {
	/** One per rubber the state has a score for, in game order. Save these first. */
	rubbers: TsrRubberSave[];
	/** The tie's own headline — rubbers won, from the rubbers above plus the ones not touched. */
	tie: {
		meet_id: string;
		body: SaveResultsBody;
		verdict: { rubbers: [number, number]; outcomes: [MeetOutcome, MeetOutcome] | null };
	};
}

/** A loaded `config` (`Record<string, unknown>`) read as the SETS config, ignoring anything malformed. */
function setsConfigOf(c: SetsMeetConfig | Record<string, unknown> | null | undefined): SetsMeetConfig | null {
	if (!c || typeof c !== "object") return null;
	const v = c as Record<string, unknown>;
	const out: SetsMeetConfig = {};
	if (isInt(v.best_of) && v.best_of >= 1) out.best_of = v.best_of;
	if (isInt(v.periods) && v.periods >= 1) out.periods = v.periods;
	if (typeof v.period_minutes === "number" && v.period_minutes > 0) out.period_minutes = v.period_minutes;
	return out;
}

/**
 * Save Meet for a team tie the controller ran as a multi-game match: game N
 * of the controller is the tie's N-th rubber by `sequence`. Each rubber with a
 * score gets its own SETS body (the rubber's sides matched to the tie's A / B
 * by competitor — a rubber's slots hold the same team competitors as the
 * tie); the tie gets rubbers won / "3–2" / WIN–LOSS from `teamTieWinner`,
 * counting the rubbers saved here with their new outcome and every other
 * rubber with the outcome it already holds. The tie's `detail` is not sent
 * (left alone). Each body goes to its own meet's `PATCH /meets/:id/results`.
 */
export function tsrTeamTieSaveBodies(input: TsrTieSaveInput): TsrTieSaveResult {
	const { state, tie } = input;
	if (!tie.slots?.[0] || !tie.slots?.[1]) throw new TsrMappingError("SLOTS_REQUIRED", "Both slots (A, B) of the tie are required");
	if (input.rubbers.length === 0) throw new TsrMappingError("NOT_A_TIE", "This meet has no rubbers");
	const idA = tie.slots[0].competitor_id;
	const idB = tie.slots[1].competitor_id;
	if (!idA || !idB) throw new TsrMappingError("SLOTS_REQUIRED", "Both sides of the tie need a competitor");

	const rubbers = [...input.rubbers].sort((x, y) => x.sequence - y.sequence || (x.meet_id < y.meet_id ? -1 : 1));

	/**
	 * `rules` is the state the rule / best-of are read from: the controller's
	 * own for the game in play; for a game already played, a tennis rubber
	 * decided by a match tie-break (a set flagged `superTiebreak`) is a doubles
	 * rubber — best of three, the third set the tie-break.
	 */
	type Game = { game: number; sets: MappedSet[]; liveSeq: number | null; finished: boolean; serving: TsrTeam | null; rules: TsrLiveMatchState };
	const games: Game[] = [];
	for (const g of state.gameHistory ?? []) {
		if (!isInt(g?.game) || g.game < 1) continue;
		const matchTiebreak = (g.sets ?? []).some((x) => x?.superTiebreak === true);
		// The controller's maxSets is the game IN PLAY's: a doubles rubber's 2 says nothing of a singles rubber before it.
		const maxSets = matchTiebreak ? 3 : state.superTiebreakDecider === true ? 0 : state.maxSets;
		const rules = { ...state, superTiebreakDecider: matchTiebreak, maxSets } as TsrLiveMatchState;
		games.push({ game: g.game, sets: bankedSets(g.sets), liveSeq: null, finished: true, serving: null, rules });
	}
	const current = currentGameSets(state);
	const currentGame = isInt(state.currentGame) && state.currentGame >= 1 ? state.currentGame : games.length + 1;
	const finished = Boolean(state.matchFinished);
	games.push({ game: currentGame, sets: current.sets, liveSeq: current.liveSeq, finished, serving: finished ? null : servingOf(state), rules: state });

	const played = games.filter((g) => g.sets.length > 0);
	const last = played.reduce((n, g) => Math.max(n, g.game), 0);
	if (last > rubbers.length) throw new TsrMappingError("RUBBERS_MISSING", `Game ${last} was played; the tie has ${rubbers.length} rubber(s)`);

	const saves: TsrRubberSave[] = [];
	const newOutcomes = new Map<string, [MeetOutcome, MeetOutcome] | null>();
	for (const g of played) {
		const rubber = rubbers[g.game - 1]!;
		const a = rubber.slots.find((s) => s.competitor_id === idA);
		const b = rubber.slots.find((s) => s.competitor_id === idB);
		if (!a || !b) throw new TsrMappingError("RUBBER_SLOTS", `Rubber ${g.game} does not hold both teams of the tie`);
		const config = setsConfigOf(rubber.config);
		const r = resolve(g.rules, config, input.sport, undefined);
		const mapped = mapMatch({ sets: g.sets, liveSeq: g.liveSeq, slots: [a, b], bestOf: r.bestOf, ends: r.ends, rule: r.rule, serving: g.serving, finished: g.finished, config });
		saves.push({ ...mapped, meet_id: rubber.meet_id, game: g.game });
		newOutcomes.set(rubber.meet_id, mapped.verdict.outcomes);
	}

	const rubberSlots = rubbers.map((r) => {
		if (!newOutcomes.has(r.meet_id)) return r.slots.map((s) => ({ competitorId: s.competitor_id, outcome: s.outcome ?? null }));
		const o = newOutcomes.get(r.meet_id) ?? null;
		return [
			{ competitorId: idA, outcome: o ? o[0] : null },
			{ competitorId: idB, outcome: o ? o[1] : null },
		];
	});
	const total = setsConfigOf(tie.config)?.best_of ?? rubbers.length;
	const verdict = teamTieWinner<string | null>([idA, idB], rubberSlots, total);
	const outcomes = verdict.outcomes as [MeetOutcome, MeetOutcome] | null;
	const decided = rubberSlots.filter((r) => r.some((s) => s.outcome !== null)).length;
	const started = decided > 0 || outcomes !== null;

	const results = ([0, 1] as const).map((i) => {
		const outcome = outcomes ? outcomes[i] : null;
		const them = i === 0 ? 1 : 0;
		const row: SaveResultsBody["results"][number] = {
			meet_competitor_id: tie.slots[i].meet_competitor_id,
			version: tie.slots[i].version,
			result_value: started ? verdict.score[i] : null,
			result_display: started ? `${verdict.score[i]}${DASH}${verdict.score[them]}` : null,
			outcome,
			rank: rankOf(outcome),
		};
		if (tie.points) row.points = pointsOf(tie.points, outcome);
		return row;
	});

	return {
		rubbers: saves,
		tie: { meet_id: tie.meet_id, body: { results }, verdict: { rubbers: [verdict.score[0], verdict.score[1]], outcomes } },
	};
}
